import { useEffect, useRef } from 'react';
import { SRD_FEATS } from '../data/srdFeats';
import { effectRegistry } from '../services/effectRegistry';
import { systemBus, EVENTS } from '../services/systemBus';

/**
 * [PURPOSE]: Automatically register feat effects with EffectRegistry when feats change
 * [WHY]: FeatsPanel is conditionally rendered, so its useEffect only runs when tab is active
 *        This hook runs in App.jsx (always mounted) to ensure effects are ALWAYS registered
 * [INTEGRATION]: Used in App.jsx to watch playerStats.feats and playerStats.customFeats
 * [ROOT CAUSE FIX]: Mobile feat not increasing speed (Line 27 - New Prompts.txt)
 *
 * Before this fix:
 * - User adds Mobile feat → registered in EffectRegistry → works until page reload
 * - User reloads page → EffectRegistry recreated with empty statIndex
 * - If user is NOT on Feats tab → FeatsPanel not mounted → useEffect never runs
 * - calculateSpeed() queries empty statIndex → no speed bonus applied!
 *
 * After this fix:
 * - This hook runs in App.jsx (always mounted)
 * - All feat effects re-registered on app startup
 * - Mobile feat speed bonus works immediately, regardless of active tab
 *
 * @param {Object} character - Full character object with feats array
 * @param {Function} logActivity - Activity logging function (optional)
 */
export const useFeatEffects = (character, logActivity = null) => {
  // [FIX APR 2026 — O2]: Track the last-logged feat-id set so we don't log duplicate
  // "Registered N feat effects" messages when the effect re-fires without a real feat change.
  // Previously this would log once per render even when feats were identical.
  const lastLoggedKey = useRef('');

  useEffect(() => {
    // [DEBUG]: Log re-registration for troubleshooting

    // [CLEANUP]: Clear old feat effects to prevent duplicates
    // removeByPrefix() removes all sources starting with 'feat:'
    effectRegistry.removeByPrefix('feat:');

    // [RE-REGISTER]: Loop through all current feats and register their effects
    const feats = character?.feats || [];

    feats.forEach(feat => {
      // [DATA LOOKUP]: Get feat definition from SRD or custom feats
      const featData = SRD_FEATS[feat.id] || character?.customFeats?.find(f => f.id === feat.id);

      // [VALIDATION]: Skip if feat data not found or no effects defined
      if (!featData) {
        console.warn(`[useFeatEffects] Feat "${feat.id}" not found in SRD or custom feats`);
        return;
      }

      // [PHASE 1.2 FORMAT CHECK]: Only register if feat uses new effects array format
      // New format: effects: [{type: 'speed', operation: 'add', value: 10}]
      // Old format: passiveEffects: {speed: 10} (handled by LEGACY path in effectCalculator)
      if (featData.effects && Array.isArray(featData.effects)) {
        try {
          // [REGISTRATION]: Register effects with source ID 'feat:featId'
          // This allows removal by prefix and debugging
          effectRegistry.register(`feat:${feat.id}`, featData.effects, character);

        } catch (error) {
          // [ERROR HANDLING]: Log but don't crash if registration fails
          console.warn(`[useFeatEffects] ❌ Failed to register effects for "${featData.name}":`, error);
        }
      } else if (featData.passiveEffects) {
        // [LEGACY FORMAT]: Log info that this feat uses old format
        // Old format feats work via LEGACY path in effectCalculator
      }

      // [Phase 4b bucket 5 leftover — APR 2026]: Process customChoices with
      // alsoGrants markers (e.g., Resilient — pick an ability + gain save proficiency
      // in it). Without this, Resilient's save proficiency was registered to a side
      // path (playerStats.savingThrowProficiencies) that calculateSavingThrow never
      // consulted. The customChoices.<field>.alsoGrants === 'saveProficiency' marker
      // tells us to register a proficiency effect with value: 'save:<picked>'.
      // [WHY]: Closes a real producer/consumer silent gap. The registry-based path
      // landed in cc81835 (calculateSavingThrow consults
      // effectRegistry.getGrantedProficiencyLevel('save:<ability>')) wasn't doing
      // anything for Resilient because no effect was being registered.
      if (featData.customChoices && feat.customChoices) {
        Object.entries(featData.customChoices).forEach(([fieldKey, choiceConfig]) => {
          if (!choiceConfig || typeof choiceConfig !== 'object') return;
          const pickedValue = feat.customChoices[fieldKey];
          if (!pickedValue) return;

          if (choiceConfig.alsoGrants === 'saveProficiency') {
            // pickedValue is the ability name ('strength', 'constitution', etc.)
            // Register a proficiency effect with the canonical save key.
            try {
              effectRegistry.register(
                `feat:${feat.id}:saveProf:${pickedValue}`,
                [{ type: 'proficiency', operation: 'grant', value: `save:${pickedValue}`, proficiencyLevel: 1 }],
                character
              );
            } catch (error) {
              console.warn(`[useFeatEffects] ❌ Failed to register save proficiency for "${featData.name}" (${pickedValue}):`, error);
            }
          }
        });
      }
    });

    // [ARC-012 FIX]: Emit event so other systems know effects changed
    systemBus.emit(EVENTS.EFFECTS_CHANGED, {
      action: 'feat_effects_registered',
      featCount: feats.length,
      source: 'useFeatEffects'
    });

    // [ACTIVITY LOG]: Log total feats re-registered, but only when the set actually changes.
    if (logActivity && feats.length > 0) {
      const registeredCount = feats.filter(feat => {
        const featData = SRD_FEATS[feat.id] || character?.customFeats?.find(f => f.id === feat.id);
        return featData?.effects && Array.isArray(featData.effects);
      }).length;

      // [FIX APR 2026 — O2]: Dedupe key = registered count + sorted feat IDs.
      // If the same set of feats re-registers (common on character load / StrictMode dev double-render),
      // don't spam the log.
      const key = `${registeredCount}:${feats.map(f => f.id).sort().join(',')}`;
      if (registeredCount > 0 && lastLoggedKey.current !== key) {
        lastLoggedKey.current = key;
        logActivity(
          `🔄 Registered ${registeredCount} feat effect${registeredCount !== 1 ? 's' : ''} with EffectRegistry`,
          'system',
          {
            totalFeats: feats.length,
            registeredEffects: registeredCount,
            featIds: feats.map(f => f.id)
          }
        );
      }
    }

  }, [character?.feats, character?.customFeats, logActivity]);
};

export default useFeatEffects;
